import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import useI18n from 'hooks/I18n/useI18n';
import { capitalizeFirstLetter } from 'modules/utils/string';
import {
    getLocalizedDateWithMonthDayWithoutYear,
    getLocalizedDay,
} from 'modules/utils/dateAndTime';
import { removeSeconds } from 'models/date';
import Link, { DISPLAY_MODES } from 'src/components/atoms/Link/Link.jsx';
import HostCard from 'components/Maps/ProductIdentity/AssembliesMap/Modal/HostCard.jsx';
import Text from 'components/ProductIdentity/Text.jsx';
import Button, { SECONDARY_MODE, XSMALL_SIZE } from 'src/components/atoms/Button/Button.jsx';

export const AssembliesCards = ({ assemblyData, selected, onChooseDate }) => {
    const { i18n, trans } = useI18n();
    const [selectedDistributionId, setSelectedDistributionId] = useState(null);

    useEffect(() => {
        if (Array.isArray(assemblyData?.openedDistributions)) {
            assemblyData.openedDistributions.sort((distribA, distribB) => {
                return distribA.timeStart > distribB.timeStart ? 1 : -1;
            });

            setSelectedDistributionId(assemblyData.openedDistributions?.[0]?.id);
        }
    }, [assemblyData]);

    const assemblyAddress = assemblyData.place.address;
    const hasDistributions = Array.isArray(assemblyData?.openedDistributions);

    const { name, host, id } = assemblyData;

    return (
        <div className={classnames('assemblies-card-container', { selected })}>
            <HostCard
                url={`/${i18n}/assemblies/${id}`}
                photoUuid={host.photoUuid}
                cityName={assemblyAddress.city.name}
                pointOfSaleName={name}
                street={assemblyAddress.street}
                hostFirstname={host.firstname}
            />

            <div className="assemblies-card-container-distribution-dates">
                {hasDistributions ? (
                    <>
                        <Text
                            size="14px"
                            weight={600}
                            className="assemblies-card-container-distribution-dates-title"
                        >
                            {capitalizeFirstLetter(
                                getLocalizedDateWithMonthDayWithoutYear(
                                    i18n,
                                    moment(assemblyData.openedDistributions[0].timeStart).toDate()
                                )
                            )}
                        </Text>
                        <ul>
                            <li>
                                {trans('assembliesMap.assembly.timeslot', {
                                    '%startHour%': moment(
                                        assemblyData.openedDistributions[0].timeStart
                                    ).format('LT'),
                                    '%endHour%': moment(
                                        assemblyData.openedDistributions[0].timeEnd
                                    ).format('LT'),
                                })}
                            </li>
                            {assemblyData.openedDistributions[0].hasHomeDelivery && (
                                <li>{trans('global.deliveredAtHome')}</li>
                            )}
                        </ul>
                    </>
                ) : (
                    <>
                        <div className="assemblies-card-container-no-distributions">
                            <Text size="12px" lineHeight="18px" color="red4">
                                {trans('distributions.follow.empty.title')}
                            </Text>
                        </div>
                        {assemblyData.deliveryTimeSlot && (
                            <>
                                <Text color="gray6" lineHeight="20px" weight={600}>
                                    {capitalizeFirstLetter(
                                        getLocalizedDay(i18n, assemblyData.deliveryTimeSlot.day)
                                    )}
                                </Text>
                                <ul className="gray-marker">
                                    <li>
                                        <Text lineHeight="20px" color="gray6">
                                            {trans('assembliesMap.assembly.timeslot', {
                                                '%startHour%': removeSeconds(
                                                    assemblyData.deliveryTimeSlot.starting
                                                ),
                                                '%endHour%': removeSeconds(
                                                    assemblyData.deliveryTimeSlot.ending
                                                ),
                                            })}
                                        </Text>
                                    </li>
                                    {assemblyData.hasHomeDelivery && (
                                        <Text lineHeight="20px" color="gray6">
                                            <li>{trans('global.deliveredAtHome')}</li>
                                        </Text>
                                    )}
                                </ul>
                            </>
                        )}
                    </>
                )}
            </div>

            {hasDistributions && (
                <div className="assemblies-card-container-button">
                    <Link
                        href={`/${i18n}/assemblies/${id}/collections/${selectedDistributionId}/products`}
                        className={classnames({ disabled: !selectedDistributionId })}
                        displayMode={DISPLAY_MODES.BUTTON}
                        bypassRouter
                    >
                        <Text>{trans('assembliesMap.modal.goToSaleBtn')}</Text>
                    </Link>
                    {assemblyData.openedDistributions.length > 1 && (
                        <Button
                            onClick={() => onChooseDate(assemblyData)}
                            size={XSMALL_SIZE}
                            mode={SECONDARY_MODE}
                        >
                            <Text>{trans('assembliesMap.button.chooseDate')}</Text>
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

AssembliesCards.propTypes = {
    assemblyData: PropTypes.object,
    selected: PropTypes.bool,
    onChooseDate: PropTypes.func,
};
AssembliesCards.defaultProps = {
    onChooseDate: () => null,
};

export default React.memo(AssembliesCards);
